import { useId, type FunctionComponent } from 'react';
import {
	activeStarThemeYellow,
	defaultStarThemeYellow,
	activeStarThemeWriteReview,
	defaultStarThemeWriteReview,
	starSmall,
	starMedium,
	starLarge
} from './star.css';

const STAR_THEME_MAP = {
	DEFAULT: { active: activeStarThemeYellow, default: defaultStarThemeYellow },
	WRITE_REVIEW: { active: activeStarThemeWriteReview, default: defaultStarThemeWriteReview }
};

const SIZE_CLASS_MAP = {
	large: starLarge,
	medium: starMedium,
	small: starSmall
};

export type StarTheme = keyof typeof STAR_THEME_MAP | null;

export type StarSize = 'small' | 'medium' | 'large';

export type StarProps = {
	averageRating: number;
	starId: number;
	starTheme?: StarTheme;
	isWriteReviewModal?: boolean;
	size?: StarSize;
};

const starCoordinatePath = `
M 165.000 234.000
L 246.0 273.217
L 230.574 185.631
L 292.393 122.283
L 205.145 107.369
L 164.000 29.000
L 122.855 107.369
L 35.607 122.283
L 97.426 185.631
L 84.649 274.217
L 168.000 237
`;

const starKey = {
	1: 'firstStar',
	2: 'secondStar',
	3: 'thirdStar',
	4: 'fourthStar',
	5: 'fifthStar'
};

export const Star: FunctionComponent<StarProps> = ({ averageRating, starId, starTheme, isWriteReviewModal, size = 'large' }) => {
	const { active: activeStar, default: defaultStar } = STAR_THEME_MAP[starTheme || 'DEFAULT'];

	const roundedRating = Math.round(averageRating * 2) / 2; // Rounded to the nearest half
	const fullStars = Math.floor(roundedRating); // Number of full stars

	const isFull = starId <= fullStars;
	const isHalf = starId === fullStars + 1 && roundedRating % 1 !== 0;

	// Avoid id collisions
	const uId = useId();
	const firstHalfMaskId = `showFirstHalf-${uId}`;
	const secondHalfMaskId = `showSecondHalf-${uId}`;
	const starSymbolId = `star-${uId}`;

	return (
		<div className="flex items-center justify-center" data-testid={`star-${starId}`}>
			{/** Defining the reusable star */}
			<svg width="0" height="0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 320">
				<defs>
					<mask id={firstHalfMaskId}>
						<rect x="0" y="0" width="320" height="320" fill="white" />
						<rect x="160" y="0" width="320" height="320" fill="black" />
					</mask>
					<mask id={secondHalfMaskId}>
						<rect x="0" y="0" width="320" height="320" fill="black" />
						<rect x="160" y="0" width="320" height="320" fill="white" />
					</mask>
					<symbol id={starSymbolId} viewBox="0 0 320 320">
						<path d={starCoordinatePath} />
					</symbol>
				</defs>
			</svg>

			<svg
				className={`${isFull || isHalf ? activeStar : defaultStar} ${SIZE_CLASS_MAP[size]}`}
				style={starId === 1 ? { marginLeft: 0 } : undefined}
				data-testid={starKey[starId]}
				viewBox="0 0 320 320">
				<use
					href={`#${starSymbolId}`}
					mask={isHalf ? `url(#${firstHalfMaskId})` : undefined}
					stroke={isWriteReviewModal ? '#f8ba1f' : ''}
				/>
				<use
					href={`#${starSymbolId}`}
					fill="none"
					stroke={isWriteReviewModal ? '#f8ba1f' : isHalf ? '#e0e0e0' : ''}
					mask={`url(#${secondHalfMaskId})`}
				/>
			</svg>
		</div>
	);
};
